import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { RefreshCw, ExternalLink, Newspaper, Zap, Clock, Search } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtTime = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : ''; } catch { return ''; } };

const CATEGORIES = [
  { key: 'hamptons',      label: 'Hamptons Updates', icon: '🌊', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { key: 'restaurants',   label: 'Restaurants',       icon: '🍽️', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { key: 'realestate',    label: 'Real Estate',        icon: '🏢', color: 'bg-teal-50 border-teal-200 text-teal-700' },
  { key: 'events',        label: 'Events',             icon: '🎉', color: 'bg-purple-50 border-purple-200 text-purple-700' },
  { key: 'hospitality',   label: 'Hospitality',        icon: '🏨', color: 'bg-green-50 border-green-200 text-green-700' },
  { key: 'liquorlicenses',label: 'Liquor Licenses',    icon: '🍷', color: 'bg-red-50 border-red-200 text-red-700' },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

function ArticleCard({ article, idx, citations }) {
  const cat = CAT_MAP[article.category] || CAT_MAP.hamptons;
  return (
    <Card className="h-full flex flex-col hover:shadow-md transition-shadow fade-in border-border">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <Badge variant="secondary" className={`text-xs shrink-0 border ${cat.color}`}>
            {cat.icon} {cat.label}
          </Badge>
          <span className="text-xs text-muted-foreground font-semibold tabular-nums">#{idx + 1}</span>
        </div>
        <CardTitle
          className="text-base leading-snug mt-2"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}
        >
          {article.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex flex-col flex-1 gap-3">
        <p className="text-sm text-foreground leading-relaxed">{article.summary}</p>
        {article.relevance && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/15">
            <Zap size={12} className="text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-primary font-medium leading-relaxed">{article.relevance}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function News() {
  const [activeCategory, setActiveCategory] = useState('hamptons');
  const [newsData, setNewsData]   = useState({});
  const [loading, setLoading]     = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchNews = useCallback(async (cat, force = false) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/news?category=${cat}&refresh=${force}`);
      setNewsData(prev => ({ ...prev, [cat]: res.data }));
      setLastFetch(new Date());
      if (force) toast.success(`${CAT_MAP[cat]?.label} refreshed via Perplexity`);
    } catch (err) {
      toast.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!newsData[activeCategory]) fetchNews(activeCategory);
  }, [activeCategory, newsData, fetchNews]);

  const current = newsData[activeCategory];
  const articles = current?.articles || [];
  const citations = current?.citations || [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Newspaper size={26} className="text-primary" />
            Live News Feed
          </h1>
          <p className="page-subtitle">
            Powered by{' '}
            <span className="font-semibold text-primary">Perplexity sonar-pro</span>
            {' '}— real-time web search grounded in current sources
            {lastFetch && <span className="ml-2 text-muted-foreground">· Updated {fmtTime(lastFetch.toISOString())}</span>}
          </p>
        </div>
        <Button
          onClick={() => fetchNews(activeCategory, true)}
          disabled={loading}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="news-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Searching...' : 'Refresh'}
        </Button>
      </div>

      {/* Perplexity badge */}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/20">
        <Search size={14} className="text-primary flex-shrink-0" />
        <span className="text-xs text-primary font-medium">Live web search</span>
        <span className="text-xs text-muted-foreground">Perplexity sonar-pro searches the web in real-time to surface the latest Hamptons intelligence — news refreshes every 10 minutes</span>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              activeCategory === cat.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            data-testid={`news-cat-${cat.key}`}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
            {newsData[cat.key] && (
              <span className={`text-xs ml-0.5 opacity-70`}>
                ({newsData[cat.key]?.articles?.length || 0})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Perplexity is searching the web for latest {CAT_MAP[activeCategory]?.label?.toLowerCase()} news...</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i} className="h-48 animate-pulse">
                <CardContent className="pt-4 space-y-3">
                  <div className="h-4 bg-muted rounded w-24" />
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Newspaper size={40} className="mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm font-medium text-foreground">No news loaded yet</p>
            <p className="text-sm text-muted-foreground mt-1">Click Refresh to search for the latest stories</p>
            <Button onClick={() => fetchNews(activeCategory, true)} className="mt-4 bg-primary">
              <Search size={14} className="mr-1.5" /> Search Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {/* Article grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {articles.map((article, idx) => (
              <ArticleCard key={idx} article={article} idx={idx} citations={citations} />
            ))}
          </div>

          {/* Sources */}
          {citations.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ExternalLink size={13} className="text-primary" />
                  Sources ({citations.length})
                  <span className="text-xs font-normal text-muted-foreground ml-1">— web sources searched by Perplexity</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2">
                  {citations.map((url, i) => {
                    let domain = url;
                    try { domain = new URL(url).hostname.replace('www.', ''); } catch {}
                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-secondary border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ExternalLink size={10} />
                        {domain}
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer meta */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Fetched: {current?.fetched_at ? fmtTime(current.fetched_at) : '—'}</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              Powered by Perplexity sonar-pro · live web search
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
